<script lang="ts">
  import SvelteTable, { type Column } from "../lib/SvelteTable.svelte";
  import PCell from "../components/cells/PCell.svelte";
  import { store } from "../helpers/store";
  import { track } from "../helpers/mixpanel";
  import type { SupplierRawInvoice } from "../helpers/types";
  import { createGetPendingInvoicesQuery } from "../queries/pending-invoices";
  import LoadingSpinner from "../components/LoadingSpinner.svelte";
  import { formatDayMonthYearTime } from "../helpers/utils";
  import InvoiceStatusCell from "../components/cells/InvoiceStatusCell.svelte";
  import UploadInvoiceEmptyState from "../components/empty-states/UploadInvoiceEmptyState.svelte";
  import TextNew from "../components/TextNew.svelte";
  import ArrowLeft from "../components/icons/ArrowLeft.svelte";
  import { selectedBuyer } from "../stores/buyer";

  $: buyerID = $selectedBuyer?.id ?? "";
  $: pendingInvoicesQuery = createGetPendingInvoicesQuery(buyerID);
  $: invoices = $pendingInvoicesQuery.data ?? [];

  function handleRowClick(event: CustomEvent) {
    window.open(event.detail.row.link);
  }

  const COLUMNS: Record<string, Column<any>> = {
    filename: {
      key: "filename",
      width: 12,
      title: "uploaded files",
      value: (v: SupplierRawInvoice) => {
        return v.filename;
      },
      renderComponent: {
        component: PCell,
        propsf(v: SupplierRawInvoice) {
          return {
            contents: this.value!(v),
          };
        },
      },
    },
    date: {
      key: "date",
      width: 6,
      title: "upload date",
      value: (v: SupplierRawInvoice) => {
        return formatDayMonthYearTime(v.created_at);
      },
      renderComponent: {
        component: PCell,
        propsf(v: SupplierRawInvoice) {
          return {
            contents: this.value!(v),
          };
        },
      },
    },
    status: {
      key: "status",
      width: 6,
      title: "status",
      value: (v: SupplierRawInvoice) => {
        return v.status;
      },
      renderComponent: {
        component: InvoiceStatusCell,
        props: {},
      },
    },
  };

  $: cols = Object.values(COLUMNS);
</script>

<div class="container">
  {#if $pendingInvoicesQuery.isLoading}
    <div class="loading-container">
      <LoadingSpinner size={60} />
    </div>
  {:else if !$pendingInvoicesQuery.isLoading && invoices.length === 0}
    <UploadInvoiceEmptyState variant="invoices" />
  {:else}
    <header>
      <a href="#/report">
        <ArrowLeft />
        <span class="title">
          <TextNew size="xl" weight="medium">back to invoices</TextNew>
        </span>
      </a>
    </header>
    <div class="table">
      <SvelteTable
        {...{
          columns: cols,
          rows: invoices,
        }}
        on:clickRow={(event) => {
          track("spend_report_processing_invoice_view_click");
          handleRowClick(event);
        }}
        classNameRow="transaction-row button"
        classNameTable="transactions-table"
        classNameCell="transaction-cell"
        sortBy="date"
        sortOrder={-1}
      />
    </div>
  {/if}
</div>

<style>
  .container {
    flex: 1;
    display: flex;
    flex-direction: column;
    background-color: white;
    height: 100vh;
    padding: 28px 24px;
  }

  .container header {
    margin-bottom: 16px;
  }

  .container header a {
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: 12px;
    text-decoration: none;
  }

  .container header .title {
    margin-top: 4px;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75vh;
  }

  .table {
    display: flex;
    flex: 1;
    margin: 16px 0;
    height: 100vh;
    overflow: auto;
    flex-direction: column;
  }
</style>
